import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { X, Smartphone, ArrowRight } from 'lucide-react';
import { detectMobilePlatform, storeUrlFor } from '@/lib/appLinks';
import { isMarketingVisit } from '@/analytics/attribution';

// Session-scoped dismiss key. Deliberately using sessionStorage (NOT
// localStorage) so the nudge reappears on every new browser session — a
// dismiss is a "not now", not a permanent opt-out.
const DISMISS_KEY = 'bower_app_install_banner_dismissed_this_session';

/**
 * "Open in Bower app" nudge for mobile browsers.
 *
 * Shown on every path (both `/app/*` and public URLs) since Universal Links
 * cover the same set. Dismiss lasts for the current browser session only —
 * open a new tab / relaunch Safari and the banner re-triggers.
 *
 * The "Open" CTA sends users straight to the App Store / Play Store. We used
 * to do a `window.location.assign(current URL)` UL-retry dance, but that's
 * pointless: if the app were installed AND the AASA/App Links verified, the
 * OS would have intercepted the original tap and we'd never have rendered
 * this banner in the first place. Since we ARE rendering, the app is
 * definitely not installed (or verification failed) — go straight to the
 * store to install it.
 *
 * Desktop / tablet / non-mobile UAs: banner never renders.
 */
export default function AppInstallBanner() {
  const { pathname } = useLocation();
  const [dismissed, setDismissed] = useState(true);
  const [platform] = useState(detectMobilePlatform);

  useEffect(() => {
    if (!platform) { setDismissed(true); return; }
    // Don't nag on auth callback routes (Google / LinkedIn / magic link) —
    // the redirect is happening in a fraction of a second and a banner
    // popping up mid-transition looks bad.
    if (pathname.startsWith('/auth/')) { setDismissed(true); return; }
    // Campaign / coupon visitors are here to book a mentor — no app nudge until they've paid
    // (the booking confirmation offers the app instead).
    if (isMarketingVisit()) { setDismissed(true); return; }
    try {
      setDismissed(sessionStorage.getItem(DISMISS_KEY) === '1');
    } catch {
      setDismissed(false);
    }
  }, [pathname, platform]);

  const storeUrl = storeUrlFor(platform);

  const openApp = () => {
    // Straight to the store. The universal-link intercept would have fired
    // already if the app were installed.
    window.location.assign(storeUrl);
  };

  const onDismiss = () => {
    try { sessionStorage.setItem(DISMISS_KEY, '1'); } catch { /* noop */ }
    setDismissed(true);
  };

  if (dismissed) return null;

  return (
    <div
      role="dialog"
      aria-label="Open in Bower app"
      data-testid="app-install-banner"
      className="fixed left-3 right-3 top-3 z-[1200] rounded-2xl border border-primary/30 bg-card/95 backdrop-blur-md shadow-lg px-3 py-2.5 flex items-center gap-3 sm:hidden"
    >
      <div className="flex-none h-10 w-10 rounded-xl bg-primary/12 flex items-center justify-center">
        <Smartphone className="h-5 w-5 text-primary" strokeWidth={2} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-[13px] font-semibold text-foreground leading-tight">Open in the Bower app</div>
        <div className="text-[11px] text-muted-foreground leading-tight mt-0.5 truncate">
          Faster, offline-friendly, and gets you notifications for sessions & events.
        </div>
      </div>
      <button
        type="button"
        onClick={openApp}
        data-testid="app-install-banner-open"
        className="flex-none inline-flex items-center gap-1 rounded-full bg-primary px-3 py-1.5 text-[12px] font-bold text-primary-foreground active:scale-[0.97] transition-transform"
      >
        Open <ArrowRight className="h-3.5 w-3.5" />
      </button>
      <button
        type="button"
        onClick={onDismiss}
        aria-label="Dismiss"
        data-testid="app-install-banner-dismiss"
        className="flex-none h-7 w-7 rounded-full border border-border text-muted-foreground hover:text-foreground flex items-center justify-center"
      >
        <X className="h-3.5 w-3.5" />
      </button>
    </div>
  );
}
